import { useMemo } from 'react';

import { gql, useQuery } from '@apollo/client';

import { NexoyaContentFilterFieldName, NexoyaContentFilterOperator } from '../../types';
import type { NexoyaContentFilterInput } from '../../types';

import { useTeam } from '../../context/TeamProvider';

/**
 * The team's ad accounts, for the label rules sheet's Channels & Accounts picker.
 * Team-wide on purpose: a label rule is on the team level.
 */

/** `ContentType.account` on the wire. Same number the impact group rule's picker asks for. */
const AD_ACCOUNT_CONTENT_TYPE = 2;

const AD_ACCOUNT_FILTERS: NexoyaContentFilterInput[] = [
  {
    fieldName: NexoyaContentFilterFieldName.ContentType,
    operator: NexoyaContentFilterOperator.Eq,
    value: { number: AD_ACCOUNT_CONTENT_TYPE },
  },
];

export const LABEL_AD_ACCOUNTS_QUERY = gql`
  query LabelAdAccounts($teamId: Float!, $filters: [ContentFilterInput!]!) {
    filterContents(teamId: $teamId, filters: $filters, excludePortfolioContents: false) {
      contentId
      title
      provider {
        provider_id
      }
    }
  }
`;

type AdAccountRow = {
  contentId: number;
  title?: string | null;
  provider?: { provider_id?: number | null } | null;
};

export type LabelAdAccountsResponse = { filterContents: AdAccountRow[] };
export type LabelAdAccountsVariables = { teamId: number; filters: NexoyaContentFilterInput[] };

/** One ad account, keyed and grouped the way a clause stores it: ids as strings. */
export type AdAccount = { accountId: string; channelId: string; title: string };

const NO_ACCOUNTS: readonly AdAccount[] = Object.freeze([]);

/**
 * Grouped by the account's DISPLAY provider, because that is the id the channel half of the
 * rule (`displayProviderId`) compares — grouping by the source provider would file an account
 * under a channel whose keyword never matches it.
 */
export function useLabelAdAccounts({ skip }: { skip?: boolean } = {}): {
  accounts: readonly AdAccount[];
  loading: boolean;
} {
  const { teamId } = useTeam();
  const { data, loading } = useQuery<LabelAdAccountsResponse, LabelAdAccountsVariables>(LABEL_AD_ACCOUNTS_QUERY, {
    variables: { teamId, filters: AD_ACCOUNT_FILTERS },
    skip: skip || !teamId,
  });

  const accounts = useMemo<readonly AdAccount[]>(() => {
    const rows = data?.filterContents;
    if (!rows?.length) return NO_ACCOUNTS;

    const next = rows
      .filter((row) => row?.contentId != null && row?.provider?.provider_id != null)
      .map((row) => ({
        accountId: String(row.contentId),
        channelId: String(row.provider.provider_id),
        title: row.title ?? '',
      }));

    return next.length ? next : NO_ACCOUNTS;
  }, [data]);

  return { accounts, loading: Boolean(loading) && !data };
}
